import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Customer } from '../types/customer';
import { SearchableSelectOption } from '../components/UI/SearchableSelect';
import { canManageCrossCustomerUsers } from '../utils/ownerCustomerAccess';
import { useToast } from '../components/UI/ToastProvider';

export function getCustomerIdFromStorage(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return (
    sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ||
    localStorage.getItem('@KnowInDoc:customer_id')?.trim() ||
    ''
  );
}

export function resolveExpectedContextTenantId(tenantId: string): string | null {
  const storageCustomerId = getCustomerIdFromStorage();
  if (!tenantId || tenantId === storageCustomerId)
  {
    return null;
  }

  return tenantId;
}

export interface UseCrossTenantSelectorOptions {
  /** Tenant ativo no serviço de contexto (ex.: chatTenantService), para detectar estado stale após remount. */
  getActiveContextTenantId?: () => string | null;
}

export function useCrossTenantSelector(
  onTenantChange?: (tenantId: string) => void | Promise<void>,
  options?: UseCrossTenantSelectorOptions
) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const canSelectTenant = canManageCrossCustomerUsers(user);
  const currentCustomerId = getCustomerIdFromStorage();
  const [tenants, setTenants] = useState<Customer[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(currentCustomerId);

  useEffect(() => {
    if (!canSelectTenant) {
      return;
    }

    let cancelled = false;
    setTenantsLoading(true);
    api
      .get<Customer[]>('/customers')
      .then(response => {
        if (cancelled) {
          return;
        }
        const activeTenants = (response.data || []).filter(tenant => tenant.isActive !== false);
        setTenants(activeTenants);
      })
      .catch(err => {
        console.error('Erro ao carregar clientes:', err);
        addToast('Erro ao carregar a lista de clientes.', 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setTenantsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canSelectTenant, addToast]);

  const tenantOptions: SearchableSelectOption[] = useMemo(
    () =>
      tenants.map(tenant => ({
        value: tenant.id,
        label: tenant.name,
        badge: tenant.isOwner ? 'Owner' : undefined,
      })),
    [tenants]
  );

  const handleTenantChange = async (tenantId: string) => {
    const expectedContextTenantId = resolveExpectedContextTenantId(tenantId);
    const activeContextTenantId = options?.getActiveContextTenantId?.() ?? null;
    const isUiStateAligned = tenantId === selectedTenantId;
    const isContextAligned = expectedContextTenantId === activeContextTenantId;

    if (isUiStateAligned && isContextAligned)
    {
      return;
    }

    setSelectedTenantId(tenantId);
    if (onTenantChange)
    {
      await onTenantChange(tenantId);
    }
  };

  const isManagingOtherTenant = Boolean(
    canSelectTenant &&
    selectedTenantId &&
    currentCustomerId &&
    selectedTenantId !== currentCustomerId
  );

  return {
    canSelectTenant,
    tenants,
    tenantsLoading,
    selectedTenantId,
    tenantOptions,
    handleTenantChange,
    currentCustomerId,
    isManagingOtherTenant,
  };
}
